import React, { useContext, useState } from 'react';
import { CSVLink } from 'react-csv';
import { PATHS } from 'src/common/constants';
import Breadcrumb from '../../components/Breadcrumb';
import HeadingWithDetails from '../../components/HeadingWithDetails';
import Skeleton from '../../components/Skeleton/Skeleton';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import PropertyDocuments from './PropertyDocuments';
import PropertyNotes from './PropertyNotes';
import PropertyServiceRequests from './PropertyServiceRequests';
import PropertySuites from './PropertySuites';
import PropertyCommonAreas from './PropertyCommonAreas';
import PropertyDevelopers from 'src/pages/Property/PropertyDevelopers/PropertyDevelopers';
import PropertyHeaderActions from './PropertyHeaderActions';
import ManageStrataManagersDialog from 'src/pages/Property/ManageStrataManagers/ManageStrataManagersDialog';
import {
  getPropertyDetailItems,
  getPropertyDetailSkeletonItems,
} from './PropertyDetailItems';
import Empty from '../../components/Empty';
import Tabs from '../../components/Tabs';
import { Colors, variables } from 'src/Tenants';
import { BrandingContext } from 'src/branding/BrandingContextProvider';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';
import { usePropertyServiceRequestsOrchestrator } from 'src/pages/Property/CustomHooks/PropertyServiceRequests/usePropertyServiceRequestsOrchestrator';
import { usePropertyPageOrchestrator } from 'src/pages/Property/CustomHooks/usePropertyPageOrchestrator/usePropertyPageOrchestrator';
import './property.scss';

let headers = [
  { label: 'Law Firm Notary', key: 'lawFirmNotary' },
  {
    label: 'Legal Representative Contact Name',
    key: 'lawyerNotaryContactName',
  },
  { label: 'Legal Representative Mailing Address', key: 'mailingAddress' },
  { label: 'Legal Representative Phone', key: 'phone' },
  { label: 'Legal Representative Email', key: 'email' },
  { label: 'Legal Representative Fax', key: 'fax' },
  { label: 'Financial Institution Bank', key: 'financialInstitutionBank' },
  { label: 'Property Name', key: 'propertyName' },
  { label: 'Suite Name', key: 'suiteName' },
  { label: 'Strata Lot', key: 'stratalot' },
  { label: 'Home Owner', key: 'homeOwner' },
  { label: 'Date', key: 'dateTime' },
];

interface NavOrigin {
  kind: 'contact';
  contactId: string;
  contactName?: string;
}

interface PropertyProps {
  match: {
    params: {
      id: any;
    };
  };
  location: {
    state?: {
      data?: {
        serviceRequestId?: any;
      };
      navOrigin?: NavOrigin;
    };
  };
  history: any;
}

const Property = (props: PropertyProps) => {
  const {
    property,
    counts,
    conveyanceCsv,
    tabList,
    suites,
    isLoadingSuites,
    refetchSuites,
    notesAPI,
    serviceRequestId,
    navOrigin,
    linkView,
    canManageProperty,
    canDeleteProperty,
    showNotesTab,
    showPeopleTab,
    showConveyanceTab,
    isPropertyFormOpen,
    propertyFormInitialData,
    propertyFormInitialModulePermissions,
    openPropertyForm,
    closePropertyForm,
    lightboxImage,
    openLightbox,
    closeLightbox,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    saveProperty,
    deleteProperty,
  } = usePropertyPageOrchestrator({
    propertyId: props.match.params.id,
    location: props.location,
  });

  const { branding } = useContext(BrandingContext);

  const [isManageStrataOpen, setIsManageStrataOpen] = useState<boolean>(false);

  const openManageStrata = (): void => {
    setIsManageStrataOpen(true);
  };

  const closeManageStrata = (): void => {
    setIsManageStrataOpen(false);
  };

  const srNavigateTo: string = PATHS.PROPERTIES_VIEW.replace(
    ':id',
    property?.propertyId ?? ''
  );

  const srNavLabel: string = property?.name ?? '';

  const srOrchestrator = usePropertyServiceRequestsOrchestrator({
    propertyId: property?.propertyId ?? '',
  });

  const srVisibleCount: number = srOrchestrator.visibleServiceRequests.length;

  const isPropertyReady: boolean = Boolean(property && property.id);

  const deletePropertyFromAPI = async (data: any) => {
    const { ok } = await deleteProperty(data.id);

    if (ok) {
      props.history.push(PATHS.PROPERTIES_LIST);
    }
  };

  const breadcrumb: React.ReactNode =
    serviceRequestId !== null ? (
      <Breadcrumb
        to={PATHS.SERVICE_REQUESTS_VIEW.replace(':id', serviceRequestId)}
        label="Service Request"
      />
    ) : navOrigin?.kind === 'contact' ? (
      <Breadcrumb
        to={PATHS.CONTACTS_VIEW.replace(':id', navOrigin.contactId)}
        label={navOrigin.contactName || 'Contact'}
      />
    ) : (
      <Breadcrumb to={PATHS.PROPERTIES_LIST} label="Properties" />
    );

  const detailItems: React.ReactNode[] = isPropertyReady
    ? getPropertyDetailItems({
        property,
        counts,
        srVisibleCount,
        serviceRequestLabel: branding?.text?.serviceRequest,
        linkView,
        onAvatarClick: openLightbox,
        onManageStrataManagers: openManageStrata,
      })
    : getPropertyDetailSkeletonItems();

  const headerActions: React.ReactNode = isPropertyReady ? (
    <PropertyHeaderActions
      property={property}
      canManageProperty={canManageProperty}
      canDeleteProperty={canDeleteProperty}
      isDeleteModalOpen={isDeleteModalOpen}
      isPropertyFormOpen={isPropertyFormOpen}
      propertyFormInitialData={propertyFormInitialData}
      propertyFormInitialModulePermissions={propertyFormInitialModulePermissions}
      tabList={tabList}
      onOpenPropertyForm={openPropertyForm}
      onClosePropertyForm={closePropertyForm}
      onSaveProperty={saveProperty}
      onOpenDeleteModal={openDeleteModal}
      onCloseDeleteModal={closeDeleteModal}
      onDeleteProperty={deletePropertyFromAPI}
    />
  ) : null;

  const tabItems: any[] = [
    {
      label: variables.suiteName,
      count: counts.suitesCount ? counts.suitesCount.toString() : '0',
      content: (
        <PropertySuites
          property={property}
          suites={suites}
          isLoadingSuites={isLoadingSuites || !isPropertyReady}
          refetchSuites={refetchSuites}
        />
      ),
    },
    {
      label: branding?.text?.serviceRequest + 's',
      count: srVisibleCount.toString(),
      content: isPropertyReady ? (
        <PropertyServiceRequests
          property={property}
          orchestrator={srOrchestrator}
          navigateTo={srNavigateTo}
          navLabel={srNavLabel}
        />
      ) : (
        <TableSkeleton />
      ),
    },
    {
      label: 'Manuals & Documents',
      count: counts.documentsCount ? counts.documentsCount.toString() : '0',
      content: isPropertyReady ? (
        <PropertyDocuments property={property} />
      ) : (
        <TableSkeleton />
      ),
    },
    {
      label: 'Common Areas',
      count: counts.commonAreasCount ? counts.commonAreasCount : '0',
      content: isPropertyReady ? (
        <PropertyCommonAreas property={property} />
      ) : (
        <TableSkeleton />
      ),
    },
    showNotesTab
      ? {
          label: 'Notes',
          count: counts.notesCount ? counts.notesCount.toString() : '0',
          content: isPropertyReady ? (
            <PropertyNotes property={property} notesAPI={notesAPI} />
          ) : (
            <TableSkeleton />
          ),
        }
      : {},
    showPeopleTab
      ? {
          label: 'People',
          count: counts.propertyDevelopersCount
            ? counts.propertyDevelopersCount.toString()
            : '0',
          content: isPropertyReady ? (
            <PropertyDevelopers property={property} />
          ) : (
            <TableSkeleton />
          ),
        }
      : {},
    showConveyanceTab
      ? {
          label: variables.conveyance,
          content: (
            <Empty
              headline="Download CSV File"
              strapline="Download a csv of the submitted legal/notary information from homeowners."
              searchIsActive={''}
              cta={
                <CSVLink
                  data={conveyanceCsv}
                  headers={headers}
                  filename={'Legal_Notary_Info.csv'}
                  className="button"
                  style={{ backgroundColor: Colors.toggleAndButtonColor }}
                  target="_blank"
                >
                  Download CSV File
                </CSVLink>
              }
              image={
                <img
                  alt="No notes yet"
                  src="/assets/download_convaynance.png"
                />
              }
            />
          ),
        }
      : {},
  ];

  return (
    <>
      <WithNavigation
        title={property?.name ?? ''}
        top={
          <HeadingWithDetails
            propertyId={props.match.params.id}
            style={{
              backgroundImage: `url(/assets/banner-property.png)`,
            }}
            breadcrumb={breadcrumb}
            headline={
              isPropertyReady ? (
                property.name
              ) : (
                <Skeleton variant="text" width="22rem" height="3.2rem" />
              )
            }
            items={detailItems}
            actions={headerActions}
          />
        }
        bottom={<Tabs from={'Property'} items={tabItems} />}
      />
      {lightboxImage && (
        <ReusableLightbox
          isOpen
          onClose={closeLightbox}
          image={lightboxImage}
        />
      )}
      {isManageStrataOpen && (
        <ManageStrataManagersDialog
          propertyId={props.match.params.id}
          isOpen
          onClose={closeManageStrata}
        />
      )}
    </>
  );
};

export default Property;
